"use client";

import { useEffect, useRef, useState } from "react";
import Image from "next/image";
import Link from "next/link";
import { ChevronRight } from "lucide-react";

export type ExploreCourseCard = {
  slug: string;
  title: string;
  badge?: string | null;
  priceText?: string | null;
};

type CertificatePathCoursesSectionProps = {
  providerName: string;
  currentCourseSlug?: string;
  courses?: ExploreCourseCard[];
};

export function CertificatePathCoursesSection({
  providerName,
  currentCourseSlug,
  courses,
}: CertificatePathCoursesSectionProps) {
  const scrollerRef = useRef<HTMLDivElement>(null);
  // The arrow only makes sense while there are more cards off to the right. With one or two
  // courses everything fits, and an arrow that scrolls nowhere just looks broken.
  const [hasMore, setHasMore] = useState(false);

  useEffect(() => {
    const el = scrollerRef.current;
    if (!el) return;
    const update = () => setHasMore(el.scrollLeft + el.clientWidth < el.scrollWidth - 4);
    update();
    el.addEventListener("scroll", update, { passive: true });
    const resize = new ResizeObserver(update);
    resize.observe(el);
    return () => {
      el.removeEventListener("scroll", update);
      resize.disconnect();
    };
  }, []);

  const scrollNext = () => {
    scrollerRef.current?.scrollBy({ left: 300, behavior: "smooth" });
  };

  const list = (courses ?? []).filter((c) => c.slug !== currentCourseSlug);
  if (!list.length) return null;

  return (
    <div className="mt-10">
      <h2 className="flex flex-wrap items-center gap-2 text-lg font-black text-slate-900 md:gap-2.5 md:text-xl">
        <span>Explore more courses offered on</span>
        <Image
          src="/images/logo.png"
          alt="InaraX"
          width={100}
          height={32}
          className="h-7 w-auto object-contain md:h-8"
        />
      </h2>
      <p className="mt-2 max-w-3xl text-sm leading-relaxed text-slate-600">
        Explore other programs you can unlock with a one-time purchase.
      </p>

      <div className="relative mt-6">
        <div
          ref={scrollerRef}
          className="-mx-1 flex snap-x snap-mandatory gap-4 overflow-x-auto pb-2 pl-1 pr-14 [scrollbar-width:none] [-ms-overflow-style:none] md:gap-5 md:pr-16 [&::-webkit-scrollbar]:hidden"
        >
          {list.map((c) => (
            <article
              key={c.slug}
              className="w-[min(100%,17.5rem)] shrink-0 snap-start md:w-72"
            >
              <Link
                href={`/catalog/${c.slug}`}
                className="block overflow-hidden rounded-xl border border-slate-200 bg-white shadow-sm transition-shadow hover:shadow-md"
              >
                <div className="relative aspect-[16/10] w-full border-l-4 border-[#0050EF] bg-slate-200">
                  <Image
                    src="/images/catalog2.png"
                    alt=""
                    fill
                    className="object-cover"
                    sizes="(min-width: 768px) 288px, 280px"
                  />
                  <div className="absolute right-2 top-2 rounded bg-white/95 p-1 shadow-sm">
                    <Image
                      src="/images/logo.png"
                      alt={providerName}
                      width={56}
                      height={18}
                      className="h-4 w-auto object-contain"
                    />
                  </div>
                  <div className="absolute bottom-2 left-2 flex items-center gap-1.5 rounded bg-black/60 px-2 py-1 text-[10px] font-semibold text-white backdrop-blur-sm">
                    <span>Created by</span>
                    <span className="text-white/95">{providerName}</span>
                  </div>
                </div>
                <div className="p-3.5">
                  {c.badge ? (
                    <div className="inline-flex max-w-full items-center gap-2 rounded border border-slate-200 bg-white px-2 py-1 text-[10px] font-semibold text-slate-700">
                      <span className="h-1.5 w-1.5 rounded-full bg-[#0050EF]" aria-hidden />
                      <span className="truncate">{c.badge}</span>
                    </div>
                  ) : null}
                  <h3 className="mt-2 line-clamp-2 text-sm font-bold leading-snug text-slate-900">{c.title}</h3>
                  <div className="mt-1 flex items-center justify-between gap-3">
                    <p className="text-xs text-slate-500">{providerName}</p>
                    {c.priceText ? (
                      <p className="text-xs font-bold text-slate-900">{c.priceText}</p>
                    ) : null}
                  </div>
                </div>
              </Link>
            </article>
          ))}
        </div>

        {hasMore ? (
          <button
            type="button"
            onClick={scrollNext}
            className="absolute right-0 top-1/2 z-10 flex h-9 w-9 -translate-y-1/2 items-center justify-center rounded-full border border-slate-200 bg-white text-slate-800 shadow-md transition hover:bg-slate-50 md:h-10 md:w-10"
            aria-label="Scroll to see more courses"
          >
            <ChevronRight className="h-5 w-5" />
          </button>
        ) : null}
      </div>

    </div>
  );
}
